import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SkillsMultiSelect from '../../components/SkillsMultiSelect';
import TextInput from '../../components/TextInput';
import CKEditorComponent from '../../components/CKEditorComponent';
import FileInput from '../../components/FileInput';
import CheckboxInput from '../../components/CheckboxInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import projectApi from '../../api/projectApi';
import { useToast } from '../../contexts/ToastContext';
import { ROUTES } from '../../constants';

const AddProject = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    excerpt: '',
    description: '',
    image: '',
    is_active: 1,
    skills: [],
  });

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : (type === 'file' ? files[0] : value),
    });
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleDescriptionChange = (content) => {
    setFormData({ ...formData, description: content });
    if (validationErrors.description) {
      setValidationErrors(prev => ({ ...prev, description: undefined }));
    }
  };

  const handleSkillChange = (selectedOptions) => {
    setFormData({ ...formData, skills: selectedOptions ? selectedOptions.map(option => option.value) : [] });
    if (validationErrors.skills) {
      setValidationErrors(prev => ({ ...prev, skills: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setValidationErrors({});

    const data = new FormData();
    for (const key in formData) {
      if (key === 'skills') {
        formData[key].forEach(skillId => data.append('skills[]', skillId));
      } else if (key === 'image') {
        if (formData[key] instanceof File) {
          data.append(key, formData[key]);
        } else if (formData[key] === null) {
          data.append(key, '');
        }
      } else {
        data.append(key, formData[key]);
      }
    }

    try {
      await projectApi.createProject(data);
      addToast('Project created successfully!', 'success');
      navigate(ROUTES.PROJECTS);
    } catch (err) {
      console.error('Failed to add project:', err);
      if (err.response && err.response.status === 422) {
        setValidationErrors(err.response.data.errors || {});
        addToast(err.response.data.message || 'Validation failed. Please check your input.', 'error');
      } else {
        addToast(err.response?.data?.message || 'Failed to add project. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white">
      <h1 className="text-3xl font-bold mb-6">Add New Project</h1>
      {loading ? (
        <SkeletonLoader type="form" />
      ) : (
        <form onSubmit={handleSubmit}>
          <TextInput
            label="Project Name"
            name="name"
            id="name"
            value={formData.name}
            onChange={handleChange}
            required={true}
            placeholder="Project Name"
            error={validationErrors?.name?.[0]}
          />
          <TextInput
            label="Excerpt"
            name="excerpt"
            id="excerpt"
            value={formData.excerpt}
            onChange={handleChange}
            required={true}
            placeholder="Short description or summary"
            error={validationErrors?.excerpt?.[0]}
          />
          <CKEditorComponent
            label="Description"
            value={formData.description}
            onChange={handleDescriptionChange}
            required={true}
            error={validationErrors?.description?.[0]}
          />
          
          <SkillsMultiSelect
            label="Skills/Technologies"
            value={formData.skills}
            onChange={handleSkillChange}
            error={validationErrors?.skills?.[0]}
            required={true}
          />

          <FileInput
            label="Project Image"
            name="image"
            id="image"
            onChange={handleChange}
            error={validationErrors?.image?.[0]}
            currentFileMessage={formData.image === null ? "No file selected" : "No current file"}
          />
          <CheckboxInput
            label="Active"
            name="is_active"
            id="is_active"
            checked={formData.is_active}
            onChange={handleChange}
          />
          <div className="flex items-center justify-between mt-6">
            <Button
              type="submit"
              className="bg-accent-red hover:bg-red-700 text-white"
              loading={loading}
            >
              Add Project
            </Button>
            <Button
              type="button"
              onClick={() => navigate(ROUTES.PROJECTS)}
              className="bg-gray-500 hover:bg-gray-600 text-white"
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AddProject;